import { useState, useMemo, useEffect, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import { onReady, trackEvent, updateAppStatusBar } from '../../utils/appBridge'
import { throttle } from '../../utils/utils'
import { motion } from 'framer-motion'
import { EliteLandingPageState, EliteOnboardingStatusResponse, EventNames } from '../../types/misc.types'
import { fetchOnboardingAdvisoryStatus } from '../../repository/misc'
import { useQuery } from '@tanstack/react-query'
import { EliteLandingPageLoader } from '../../components/elite/landing-page/EliteLandingPageLoader'
import { EliteButtonV3 } from '../../components/elite/EliteButtonV3'
import { getEliteConsentScrollSpacerClass, hasEliteConsentText } from '../../components/elite/EliteConsentTextContent'
import { EliteLandingPageBodyV3 } from '../../components/elite/landing-page/versions/landing-page-body/EliteLandingPageBodyV3'
import { EliteLandingPageHeadV7 } from '../../components/elite/landing-page/versions/landing-page-head/EliteLandingPageHeadV7'
import { getBottomPadding } from '../../utils/sessionStorage'
import { ElitePageBackBar } from '../../components/elite/ElitePageBackBar'
import { getEliteBuyCtaEventExtras } from '../../utils/eliteEventExtras'
import { EliteBuyCtaExtrasProvider } from '../../components/elite/EliteBuyCtaExtrasContext'
import { EliteAboutVideoFab } from '../../components/elite/EliteAboutVideoFab'
import { useIsClearOfBottomFabZone } from '../../components/elite/useIsClearOfBottomFabZone'

const ELITE_LANDING_PAGE_VERSION = 'V4'

export const EliteLandingPageV7 = () => {
  const [isEliteButtonVisible, setIsEliteButtonVisible] = useState(false)
  const [isEliteButtonDisabled, setIsEliteButtonDisabled] = useState(false)
  const [eliteOnboardingData, setEliteOnboardingData] = useState<EliteOnboardingStatusResponse>()
  const onReadyLock = useRef<boolean>(false)
  const floatingEliteCtaRef = useRef<HTMLDivElement>(null)
  const inlineEliteCtaRef = useRef<HTMLDivElement>(null)
  const scrollRootRef = useRef<HTMLDivElement>(null)

  const throttledEliteButtonVisibility = throttle((eliteButtonVisibility: boolean) => {
    setIsEliteButtonVisible(eliteButtonVisibility)
  }, 0)
  const handleEliteButtonVisibility = (eliteButtonVisibility: boolean) =>
    throttledEliteButtonVisibility(eliteButtonVisibility)

  const [searchParams] = useSearchParams()

  const pageType = useMemo(() => {
    return searchParams?.get('pageType') ?? ''
  }, [searchParams])

  const { data, isSuccess, isLoading, isError } = useQuery<any, Error>({
    queryKey: ['ONBOARDING_ADVISORY_STATUS'],
    queryFn: fetchOnboardingAdvisoryStatus,
    retry: 1,
    enabled: pageType !== 'no-upgrade',
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    refetchOnReconnect: true
  })
  const showAboutVideoFab = useIsClearOfBottomFabZone(inlineEliteCtaRef, scrollRootRef, !isLoading)

  useEffect(() => {
    if (isSuccess) {
      const dataCopy = data?.data as EliteOnboardingStatusResponse
      setEliteOnboardingData(() => dataCopy)
    }
  }, [isSuccess])

  const handlePageReady = () => {
    if (!onReadyLock.current) {
      onReadyLock.current = true
      onReady()
    }
  }

  useEffect(() => {
    setTimeout(() => {
      handlePageReady()
    }, 1000)
    updateAppStatusBar('000000', 'light')
  }, [])

  useEffect(() => {
    if (!isSuccess && !isError) return
    trackEvent({
      eventName: EventNames.ScreenLoaded,
      payload: {
        ...getEliteBuyCtaEventExtras(eliteOnboardingData ?? (data?.data as EliteOnboardingStatusResponse | undefined)),
        eventId: ELITE_LANDING_PAGE_VERSION,
        screenType: 'MF_Elite_Landing'
      }
    })
  }, [isSuccess, isError])

  useEffect(() => {
    if (isSuccess || isError) setTimeout(() => handlePageReady(), 150)
  }, [isSuccess, isError])

  const onBGRiveLoad = () => { }

  const eliteLandingPageState = useMemo(() => {
    return eliteOnboardingData?.eliteLandingPageState ?? EliteLandingPageState.ELITE
  }, [eliteOnboardingData])

  useEffect(() => {
    setIsEliteButtonDisabled(
      () =>
        eliteLandingPageStateConfig?.[eliteLandingPageState]?.isDisabled ||
        !eliteOnboardingData?.cta ||
        eliteLandingPageState === EliteLandingPageState.FAMILY_ONBOARDING_NOT_ALLOWED
    )
  }, [eliteLandingPageState])

  if (isLoading) return <EliteLandingPageLoader />

  return (
    <EliteBuyCtaExtrasProvider status={eliteOnboardingData ?? (data?.data as EliteOnboardingStatusResponse | undefined)}>
    <div className={`h-screen flex flex-col relative overflow-hidden bg-[#010101] w-full`}>
      <EliteLandingPageV7BackBar isEliteButtonVisible={isEliteButtonVisible} />

      <div
        ref={scrollRootRef}
        className='flex-1 min-h-0 w-full overflow-x-hidden overflow-y-auto overscroll-none no-scrollbar'
      >
        <div className='relative z-0 flex h-max w-full flex-col'>
          <EliteLandingPageHeadV7
            handleEliteButtonVisibility={handleEliteButtonVisibility}
            eliteLandingPageState={eliteOnboardingData?.eliteLandingPageState ?? EliteLandingPageState.ELITE}
            amount={eliteOnboardingData?.userSubscriptionDetails?.membershipFees?.amount ?? 0}
            cta={eliteOnboardingData?.cta ?? null}
            isForceUpdateRequired={eliteOnboardingData?.isForceUpdateRequired ?? false}
            isEliteButtonDisabled={isEliteButtonDisabled}
            onBGRiveLoad={onBGRiveLoad}
            version={ELITE_LANDING_PAGE_VERSION}
            activationExpiryDate={eliteOnboardingData?.userSubscriptionDetails?.activationExpiryDate ?? undefined}
            isEliteButtonVisible={isEliteButtonVisible}
            consentText={eliteOnboardingData?.consentText}
            consentTextList={eliteOnboardingData?.consentTextList}
            familyMemberName={eliteOnboardingData?.secondaryUserName}
            pageType={pageType}
            referralReward={eliteOnboardingData?.referralReward ?? null}
            inlineEliteCtaRef={inlineEliteCtaRef}
          />
          <div className='bg-[#FEFFFE] z-10'>
            <EliteLandingPageBodyV3 pageType={pageType} />
          </div>
          {pageType !== 'no-upgrade' && !isEliteButtonDisabled && isEliteButtonVisible ? (
            <div
              aria-hidden
              className={getEliteConsentScrollSpacerClass(
                eliteOnboardingData?.consentText,
                eliteOnboardingData?.consentTextList
              )}
            />
          ) : null}
        </div>
      </div>
      {pageType !== 'no-upgrade' && !isEliteButtonDisabled && isEliteButtonVisible && (
        <motion.div
          ref={floatingEliteCtaRef}
          initial={{ y: '100%' }}
          animate={{ y: 0 }}
          transition={{ type: 'tween', stiffness: 300, damping: 25, duration: 0.25 }}
          style={{ bottom: `${getBottomPadding({ includeExtraPadding: false })}px` }}
          className={`pointer-events-auto fixed left-0 right-0 z-50 px-5 pt-5 pb-10 animate-fade-150 flex w-full 
            bg-gradient-to-b from-[rgba(255,255,255,0)] via-[rgba(255,255,255,0.3)] to-[rgba(255,255,255,0.8)] 
            ${hasEliteConsentText(eliteOnboardingData?.consentTextList, eliteOnboardingData?.consentText) ? '!bg-white !pt-8' : ''}`}
        >
          <EliteButtonV3
            eliteLandingPageState={eliteLandingPageState}
            amount={eliteOnboardingData?.userSubscriptionDetails?.membershipFees?.amount ?? 0}
            cta={eliteOnboardingData?.cta ?? null}
            isForceUpdateRequired={eliteOnboardingData?.isForceUpdateRequired ?? false}
            version={ELITE_LANDING_PAGE_VERSION}
            theme={'INVERTED_ELITE'}
            consentText={eliteOnboardingData?.consentText}
            consentTextList={eliteOnboardingData?.consentTextList}
            isEliteButtonVisible={isEliteButtonVisible}
            familyMemberName={eliteOnboardingData?.secondaryUserName}
            isButtonDisabled={eliteLandingPageState === EliteLandingPageState.FAMILY_ONBOARDING_NOT_ALLOWED}
            referralReward={eliteOnboardingData?.referralReward ?? null}
          />
        </motion.div>
      )}
      {pageType !== 'no-upgrade' ? (
        <EliteAboutVideoFab
          showFab={showAboutVideoFab}
          isEliteCtaVisible={!isEliteButtonDisabled && isEliteButtonVisible}
          eliteCtaRef={floatingEliteCtaRef}
        />
      ) : null}
    </div>
    </EliteBuyCtaExtrasProvider>
  )
}

export function EliteLandingPageV7BackBar({
  isEliteButtonVisible
}: {
  isEliteButtonVisible: boolean
}) {
  return (
    <ElitePageBackBar isEliteButtonVisible={isEliteButtonVisible} />
  )
}

const UPGRADE_TO_ELITE_BUTTON_TEXT = `Upgrade to Elite @ <span style="font-weight: 700;">
                 ₹999/yr + GST </span>`

export const eliteLandingPageStateConfig: {
  [key in `${EliteLandingPageState}`]?: {
    buttonText: string
    supportText?: string
    topText?: string
    isDisabled?: true
  }
} = {
  [EliteLandingPageState.ONB_NOT_INITIATED]: {
    buttonText: UPGRADE_TO_ELITE_BUTTON_TEXT,
    topText: 'Special price for first 10k members'
  },
  [EliteLandingPageState.ONB_IN_PROGRESS]: {
    buttonText: UPGRADE_TO_ELITE_BUTTON_TEXT,
    topText: 'Special price for first 10k members'
  },
  [EliteLandingPageState.PAYMENT_NOT_INITIATED]: {
    buttonText: UPGRADE_TO_ELITE_BUTTON_TEXT,
    topText: 'Special price for first 10k members'
  },
  [EliteLandingPageState.PAYMENT_PENDING]: {
    buttonText: UPGRADE_TO_ELITE_BUTTON_TEXT,
    isDisabled: true,
    supportText:
      'Awaiting payment confirmation from your bank. If payment fails, the money will be refunded within 3 working days.'
  },
  [EliteLandingPageState.RE_INITIATE_PAYMENT]: {
    buttonText: 'Retry Payment',
    supportText:
      'Your last payment has failed. Any money deducted will be refunded within 3 working days. You can retry payment to complete your purchase.'
  },
  [EliteLandingPageState.REFUND_PENDING]: {
    buttonText: UPGRADE_TO_ELITE_BUTTON_TEXT,
    topText: 'Special price for first 10k members',
    supportText: 'Your membership has been cancelled. Refund will be credited in 5-7 working days.'
  },
  [EliteLandingPageState.PAYMENT_COMPLETED_SUBSCRIPTION_PENDING]: {
    buttonText: 'Complete eSign',
    supportText: 'Your payment was successful. Complete eSign to activate your Elite membership.',
    topText: 'Special price for limited time only'
  },
  [EliteLandingPageState.PAYMENT_COMPLETED_SUBSCRIPTION_PENDING_WARNING]: {
    buttonText: 'Complete eSign',
    supportText:
      'Your payment was successful. Complete eSign before {activationExpiryDate} to activate your Elite membership.',
    topText: 'Special price for limited time only'
  },
  [EliteLandingPageState.REFUND_DUE_TO_ESIGN_PENDING]: {
    buttonText: UPGRADE_TO_ELITE_BUTTON_TEXT,
    supportText:
      'Your Elite membership could not be activated as the eSign was not completed in time. The refund has been initiated and will be credited to source in 3-5 days.',
    topText: 'Special price for limited time only'
  },
  [EliteLandingPageState.FAMILY_ONBOARDING_NOT_ALLOWED]: {
    buttonText: 'Upgrade to Elite',
    topText: 'You need to be an Elite member to make this purchase for {familyMemberName}'
  },
  [EliteLandingPageState.ELITE]: {
    isDisabled: true,
    buttonText: 'Something went wrong'
  }
}
